// Phase 1A onboarding step metadata — single source of truth.
//
// AuthGate, OnboardingShell-consuming pages, and any other surface that
// reflects onboarding progress all read from this file. Adding or removing
// a step updates the indicator dots, AuthGate routing, and any "on an
// onboarding route?" check at the same time. Prevents the totalSteps count
// or route strings from drifting between files.
//
// `enabled: false` means the step is part of the canonical Phase 1A plan
// but its UI is not built yet. The step still consumes a dot in the
// indicator (so the user can see how much onboarding is left), but
// AuthGate does not redirect to disabled routes.

export type OnboardingStepKey = "age" | "jurisdiction" | "safe_mode" | "platform" | "phantom";

export interface OnboardingStep {
  key: OnboardingStepKey;
  route: string;
  label: string;
  enabled: boolean;
}

export const PHASE_1A_ONBOARDING_STEPS: readonly OnboardingStep[] = [
  { key: "age", route: "/onboarding/age", label: "Age", enabled: true },
  { key: "jurisdiction", route: "/onboarding/jurisdiction", label: "Jurisdiction", enabled: true },
  { key: "safe_mode", route: "/onboarding/safe-mode", label: "Safe Mode", enabled: true },
  { key: "platform", route: "/onboarding/platform", label: "Platform", enabled: true },
  { key: "phantom", route: "/onboarding/phantom", label: "Phantom Bet", enabled: true },
] as const;

export const TOTAL_ONBOARDING_STEPS = PHASE_1A_ONBOARDING_STEPS.length;

// Routes whose UI exists today. AuthGate uses this to detect when a user
// is sitting on an already-completed onboarding route and should be
// forwarded to the dashboard. Disabled routes are excluded so AuthGate
// never redirects to a 404.
export const ENABLED_ONBOARDING_ROUTES: readonly string[] = PHASE_1A_ONBOARDING_STEPS.filter(
  (step) => step.enabled,
).map((step) => step.route);

export function getStepNumber(key: OnboardingStepKey): number {
  const idx = PHASE_1A_ONBOARDING_STEPS.findIndex((step) => step.key === key);
  if (idx === -1) {
    throw new Error(`Unknown onboarding step key: ${key}`);
  }
  return idx + 1;
}

export function getStepRoute(key: OnboardingStepKey): string {
  const step = PHASE_1A_ONBOARDING_STEPS.find((s) => s.key === key);
  if (!step) {
    throw new Error(`Unknown onboarding step key: ${key}`);
  }
  return step.route;
}
